import { Card, CardContent, IconButton, Typography } from '@material-ui/core';
import { Add, Edit, Send } from '@material-ui/icons';
import { formatDate, FormattedDate } from '@superdispatch/dates';
import { formatPaymentTerm } from '@superdispatch/sdk';
import { CardButton, Column, Columns, Inline, Stack } from '@superdispatch/ui';
import { DescriptionItem } from '@superdispatch/ui-lab';
import { useMemo, useState } from 'react';
import { BankErrorSimpleBanner } from 'shared/errors/SuperPayBankErrors';
import { isEmptyRecord } from 'shared/helpers/CommonHelpers';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import { SuperPayLabel } from 'shared/helpers/PaymentHelpers';
import { SuperPayNudgingBanner } from 'shared/modules/superpay/SuperPayNudgingBanner';
import {
  useCarrierPaidMethodOptions,
  useCarrierSettings,
} from 'shared/settings/CarrierSettingsAPI';
import { InlineBulletItems } from 'shared/ui/InlineBulletItems';
import { HiddenForPrint } from 'shared/ui/Print';
import { computePrice } from 'shared/utils/NumberUtils';
import { EditPaymentDrawer } from '../core/EditLoadPaymentDrawer';
import { LoadPaymentStatus } from '../core/LoadPaymentStatus';
import { castAPIData } from 'shared/api/APISchema';
import { LoadDTO, loadPaymentSchema } from '../data/LoadDTO';
import { ViewLoadLegNotes } from './ViewLoadLegNotes';
import { ViewLoadPaymentMethod } from './ViewLoadPaymentMethod';
import { ViewLoadPaymentCardRow } from './ViewLoadPaymentRenderCardRow';
import { ViewLoadPaymentDue } from './ViewLoadPaymentDue';

interface ViewLoadPaymentProps {
  load: LoadDTO;
  layout?: 'print';
  isEnabledEditLoad?: boolean;
}

const defaultValuePlaceholder = 'No details';

export function ViewLoadPayment({
  load,
  layout,
  isEnabledEditLoad = true,
}: ViewLoadPaymentProps) {
  const { data: settings } = useCarrierSettings();
  const paidMethodOptions = useCarrierPaidMethodOptions();
  const payment = useMemo(
    () =>
      load.payments && load.payments.length > 0
        ? castAPIData(loadPaymentSchema, load.payments[0])
        : null,
    [load.payments],
  );

  const [modalState, setModalState] = useState<'edit'>();

  const renderNotes = (notes: string, e2eValue?: string) => (
    <Stack>
      <Typography color="textSecondary">Notes</Typography>
      <ViewLoadLegNotes
        content={notes}
        data-e2e={e2eValue}
        disableTruncate={layout === 'print'}
      />
    </Stack>
  );

  const openDrawer = () => {
    setModalState('edit');
  };

  const isEmpty = useMemo(() => {
    if (!payment) return false;
    const { invoice, price, payment_details, ...paymentDetails } = payment;
    return !price && isEmptyRecord(invoice) && isEmptyRecord(paymentDetails);
  }, [payment]);

  const vehiclesTotalPrice = useMemo(
    () => computePrice(load.vehicles),
    [load.vehicles],
  );

  const paidMethod = useMemo(
    () => !!payment?.paid_method && paidMethodOptions.get(payment.paid_method),
    [payment?.paid_method, paidMethodOptions],
  );

  return (
    <>
      <EditPaymentDrawer
        loadGUID={load.guid}
        open={modalState === 'edit'}
        isEnabledEditLoad={isEnabledEditLoad}
        onClose={() => {
          setModalState(undefined);
        }}
      />

      {isEmpty ? (
        layout !== 'print' && (
          <HiddenForPrint>
            <CardButton
              startIcon={<Add />}
              onClick={openDrawer}
              aria-label="Add payment"
              disabled={!load.can_be_edited}
            >
              Add payment
            </CardButton>
          </HiddenForPrint>
        )
      ) : (
        <Card
          aria-label="Payment Details"
          elevation={0}
          variant={layout === 'print' ? 'elevation' : 'outlined'}
        >
          <CardContent>
            <Stack space="large">
              <Stack space="small">
                <Stack>
                  <Columns space="xxsmall">
                    <Column width="fluid">
                      <Stack>
                        <Typography variant="h3" color="textPrimary">
                          Payment
                        </Typography>
                        {payment?.method === 'superpay' &&
                          (settings?.superpay.payment_error ? (
                            <BankErrorSimpleBanner
                              source="Load Details"
                              paymentError={settings.superpay.payment_error}
                            />
                          ) : (
                            <SuperPayNudgingBanner source="Load Details" />
                          ))}
                      </Stack>
                    </Column>

                    {layout !== 'print' && load.can_be_edited && (
                      <Column width="content">
                        <IconButton
                          size="small"
                          onClick={openDrawer}
                          aria-label="Edit Payment Button"
                        >
                          <Edit />
                        </IconButton>
                      </Column>
                    )}
                  </Columns>

                  <Stack>
                    <LoadPaymentStatus
                      payment={payment}
                      loadGUID={load.guid}
                      source="Load Details"
                    />

                    {!!payment?.invoice.sent_at && (
                      <DescriptionItem
                        icon={<Send />}
                        label={
                          <>
                            Invoiced on{' '}
                            <FormattedDate
                              date={payment.invoice.sent_at}
                              variant="DateTime"
                            />
                          </>
                        }
                      />
                    )}
                  </Stack>
                </Stack>

                <Stack space="xsmall">
                  <Stack space="none">
                    <ViewLoadPaymentCardRow
                      title="Price"
                      e2eValue="payment.price"
                      value={
                        (payment?.price && formatCurrency(payment.price)) ||
                        defaultValuePlaceholder
                      }
                      boldValue={true}
                    />
                    {!!vehiclesTotalPrice &&
                      vehiclesTotalPrice !== payment?.price && (
                        <Typography color="textSecondary" align="right">
                          Vehicles Total {formatCurrency(vehiclesTotalPrice)}
                        </Typography>
                      )}
                  </Stack>

                  <ViewLoadPaymentMethod load={load} />

                  <ViewLoadPaymentCardRow
                    title="Terms"
                    e2eValue="payment.payment_terms"
                    value={
                      load.payment_details?.payment_terms
                        ? formatPaymentTerm(load.payment_details.payment_terms)
                        : defaultValuePlaceholder
                    }
                  />

                  {!!payment?.instant_transfer_fee_amount && (
                    <ViewLoadPaymentCardRow
                      title="Instant Transfer Fee"
                      e2eValue="payment.instant_transfer_fee_amount"
                      value={
                        <Inline space="xxsmall">
                          <Typography>
                            {formatCurrency(
                              payment.instant_transfer_fee_amount,
                            )}
                          </Typography>
                          <Typography color="textSecondary">
                            ({payment.instant_transfer_fee_percentage}%)
                          </Typography>
                        </Inline>
                      }
                    />
                  )}

                  <ViewLoadPaymentCardRow
                    title="Broker Fee"
                    e2eValue="payment.broker_fee"
                    value={
                      (payment?.broker_fee &&
                        formatCurrency(payment.broker_fee)) ||
                      defaultValuePlaceholder
                    }
                  />
                  {payment?.method === 'superpay' && payment.payment_due ? (
                    <ViewLoadPaymentDue
                      title="Payment Due"
                      e2eValue="payment.payment_due"
                      value={formatDate(payment.payment_due, {
                        variant: 'Date',
                      })}
                      paymentStatus={payment.status}
                    />
                  ) : null}
                  {!load.is_received_via_subhaul && (
                    <ViewLoadPaymentCardRow
                      title="Driver Pay"
                      e2eValue="payment.driver_pay"
                      value={
                        (payment?.driver_pay &&
                          formatCurrency(payment.driver_pay)) ||
                        defaultValuePlaceholder
                      }
                    />
                  )}
                </Stack>

                {!!payment?.notes &&
                  renderNotes(payment.notes, 'payment.notes')}
              </Stack>

              {!!payment?.paid_at && (
                <Stack>
                  <ViewLoadPaymentCardRow
                    title="Paid Amount"
                    e2eValue="payment.paid_amount"
                    value={
                      payment.paid_amount
                        ? formatCurrency(payment.paid_amount)
                        : defaultValuePlaceholder
                    }
                    boldValue={true}
                  />

                  <ViewLoadPaymentCardRow
                    title="Method"
                    e2eValue="payment.paid_method"
                    value={
                      !paidMethod ? (
                        defaultValuePlaceholder
                      ) : paidMethod.toLowerCase() === 'superpay' ? (
                        <InlineBulletItems>
                          <SuperPayLabel source="Load Details" />
                          {payment.terms}
                        </InlineBulletItems>
                      ) : (
                        paidMethod
                      )
                    }
                  />
                  <ViewLoadPaymentCardRow
                    title="Receipt Date"
                    e2eValue="payment.receipt_date"
                    value={
                      (payment.paid_at &&
                        formatDate(payment.paid_at, { variant: 'DateTime' })) ||
                      defaultValuePlaceholder
                    }
                  />
                  <ViewLoadPaymentCardRow
                    title="Reference Number"
                    e2eValue="payment.payment_reference_number"
                    value={
                      payment.payment_reference_number ||
                      defaultValuePlaceholder
                    }
                  />
                </Stack>
              )}

              {!isEmptyRecord(payment?.invoice) && (
                <Stack>
                  {!!payment?.invoice.invoice_id && (
                    <Typography variant="h4" data-e2e="payment.invoice.title">
                      Invoice #{payment.invoice.invoice_id}
                    </Typography>
                  )}
                  {!!payment?.invoice.invoice_notes &&
                    renderNotes(
                      payment.invoice.invoice_notes,
                      'payment.invoice_notes',
                    )}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </>
  );
}
